import React, { useState } from 'react';
import Box from '@amzn/awsui-components-react/polaris/box';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
import Button from '@amzn/awsui-components-react/polaris/button';
import Container from '@amzn/awsui-components-react/polaris/container';
import Header from '@amzn/awsui-components-react/polaris/header';
import Alert from '@amzn/awsui-components-react/polaris/alert';

interface Question {
  text: string;
  yes?: string;
  no?: string;
  action?: boolean;
}

interface Questions {
  [key: string]: Question;
}

interface HistoryItem {
  question: string;
  answer: string;
}

const EngineeringDecisionTree: React.FC = () => {
  const [step, setStep] = useState('site_launch');
  const [history, setHistory] = useState<HistoryItem[]>([]);

  const questions: Questions = {
    site_launch: {
      text: 'Is FGID in FGDS?',
      yes: 'sitelaunch_match',
      no: 'cut_ticket_fgds',
    },
    sitelaunch_match: {
      text: 'Does site launch date match business start date in FGDS?',
      yes: 'demand_data',
      no: 'cut_ticket_fgds_correct',
    },
    demand_data: {
      text: 'Does it have demand data?',
      yes: 'inventory_data',
      no: 'create_test_order',
    },
    inventory_data: {
      text: 'Does it have inventory data?',
      yes: 'check_inventory_asin',
      no: 'wait_bootstrap',
    },
    check_inventory_asin: {
      text: 'Does inventory have more than one ASIN?',
      yes: 'good_to_go',
      no: 'end',
    },
    cut_ticket_fgds: {
      text: 'Cut a ticket to FGDS to create FG',
      action: true,
    },
    cut_ticket_fgds_correct: {
      text: 'Cut a ticket to FGDS to correct date',
      action: true,
    },
    create_test_order: {
      text: 'Create test order',
      action: true,
    },
    wait_bootstrap: {
      text: 'Wait for bootstrap process to be completed',
      action: true,
    },
    good_to_go: {
      text: 'Good to go',
      action: true,
    },
    end: {
      text: 'End of process',
      action: true,
    },
  };

  const handleAnswer = (answer: string) => {
    const currentQuestion = questions[step];
    setHistory([...history, { question: currentQuestion.text, answer }]);

    if (currentQuestion[answer as keyof typeof currentQuestion]) {
      setStep(currentQuestion[answer as keyof typeof currentQuestion] as string);
    } else {
      setStep('done');
    }
  };

  const resetQuestions = () => {
    setStep('site_launch');
    setHistory([]);
  };

  const currentQuestion = questions[step];

  if (step === 'done') {
    return (
      <Container>
        <SpaceBetween size="l">
          <Header variant="h1">Decision Process Complete</Header>
          <Box>
            <SpaceBetween size="l">
              {history.map((item, index) => (
                <Alert key={index}>
                  <Box>
                    <strong>{item.question}</strong>
                    <br />
                    Answer: {item.answer}
                  </Box>
                </Alert>
              ))}
              <Button onClick={resetQuestions} variant="primary">
                Start Over
              </Button>
            </SpaceBetween>
          </Box>
        </SpaceBetween>
      </Container>
    );
  }

  return (
    <Container>
      <SpaceBetween size="l">
        <Header variant="h1">Store Launch Readiness</Header>
        <Box>
          <SpaceBetween size="l">
            <Header variant="h2">{currentQuestion.text}</Header>
            {!currentQuestion.action ? (
              <SpaceBetween size="xs" direction="horizontal">
                <Button onClick={() => handleAnswer('yes')} variant="primary">
                  Yes
                </Button>
                <Button onClick={() => handleAnswer('no')} variant="normal">
                  No
                </Button>
              </SpaceBetween>
            ) : (
              <Box>
                <SpaceBetween size="l">
                  <Alert type="info">
                    Action required: {currentQuestion.text}
                  </Alert>
                  <Button onClick={() => handleAnswer('next')} variant="primary">
                    Next
                  </Button>
                </SpaceBetween>
              </Box>
            )}
          </SpaceBetween>
        </Box>
      </SpaceBetween>
    </Container>
  );
};

export default EngineeringDecisionTree;
